'use client';

import React, { useState, useTransition } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import InfiniteScroll from 'react-infinite-scroll-component';
import ArrowLeftSLineIcon from 'remixicon-react/ArrowLeftSLineIcon';
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon';
import Spinner from '@/common/components/spinner/spinner.component';

interface HybridPaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  stopAtPage?: number;
  listLength: number;
  scrollThreshold?: number | string;
  children: React.ReactNode;
  loader?: React.ReactNode;
  isFetching?: boolean;
  replaceOnNavigate?: boolean;
  onPageChange?: (page: number, mode: 'append' | 'replace') => void;
}

type PaginationItem = number | `ellipsis-${number}`;

function getPaginationItems(currentPage: number, totalPages: number) {
  const pages = Array.from(
    new Set([
      1,
      ...(currentPage <= 4 ? [2, 3, 4, 5, 6] : []),
      currentPage - 1,
      currentPage,
      currentPage + 1,
      totalPages,
    ])
  )
    .filter((page) => page > 0 && page <= totalPages)
    .sort((first, second) => first - second);

  return pages.reduce<PaginationItem[]>((items, page, index) => {
    const previous = pages[index - 1];
    if (previous !== undefined && page - previous > 1) {
      items.push(`ellipsis-${previous}`);
    }
    items.push(page);
    return items;
  }, []);
}

export function HybridPagination({
  currentPage,
  totalItems,
  children,
  listLength,
  scrollThreshold = 0.9,
  stopAtPage = 5,
  pageSize,
  loader,
  isFetching = false,
  replaceOnNavigate = false,
  onPageChange,
}: HybridPaginationProps) {
  const [isNavigating, startTransition] = useTransition();
  // Only flip to classic pagination via explicit page clicks — never because
  // the component remounted on page 2+ during infinite scroll.
  const [isPaginationMode, setIsPaginationMode] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const navigateToPage = (page: number, mode: 'append' | 'replace') => {
    const params = new URLSearchParams(searchParams.toString());
    if (page <= 1) params.delete('page');
    else params.set('page', String(page));
    onPageChange?.(page, mode);
    const query = params.toString();
    startTransition(() => {
      const href = query ? `${pathname}?${query}` : pathname;
      if (replaceOnNavigate) {
        router.replace(href, { scroll: false });
      } else {
        router.push(href, { scroll: false });
      }
    });
  };

  const getPageHref = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (page <= 1) params.delete('page');
    else params.set('page', String(page));
    const query = params.toString();
    return query ? `${pathname}?${query}` : pathname;
  };

  const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));
  const infiniteStopPage = Math.min(stopAtPage, lastPage);
  const canLoadMorePages = !isPaginationMode && currentPage < infiniteStopPage;
  const isInfiniteScrollEnabled =
    canLoadMorePages && !isFetching && !isNavigating;
  const shouldShowPagination = lastPage > 1;
  const pages = getPaginationItems(currentPage, lastPage);
  const showLoader = canLoadMorePages && (isNavigating || isFetching);

  return (
    <div>
      <InfiniteScroll
        dataLength={listLength}
        scrollThreshold={scrollThreshold}
        next={() => {
          if (isInfiniteScrollEnabled && !isNavigating) {
            navigateToPage(currentPage + 1, 'append');
          }
        }}
        hasMore={isInfiniteScrollEnabled}
        loader={null}
        endMessage={<></>}
      >
        {children}
      </InfiniteScroll>

      {showLoader && (
        <div
          role="status"
          aria-live="polite"
          className="flex h-20 items-center justify-center py-3"
        >
          {loader ?? (
            <Spinner
              containerClassName="h-full"
              spinnerClassName="!h-8 !w-8 !text-plp-accent"
            />
          )}
          <span className="sr-only">در حال دریافت اطلاعات بیشتر</span>
        </div>
      )}

      {shouldShowPagination && (
        <nav
          aria-label="صفحه‌بندی محصولات"
          className="mt-12 flex items-center justify-center gap-3 pb-4 pt-4 md:mt-16 md:gap-4"
        >
          <button
            type="button"
            aria-label="صفحه قبلی"
            disabled={currentPage === 1 || isNavigating}
            onClick={() => {
              setIsPaginationMode(true);
              navigateToPage(Math.max(1, currentPage - 1), 'replace');
            }}
            className="grid size-8 place-items-center rounded-full bg-white text-plp-muted disabled:pointer-events-none disabled:opacity-40"
          >
            <ArrowRightSLineIcon className="size-4" />
          </button>

          {pages.map((page) => {
            if (typeof page !== 'number') {
              return (
                <span
                  key={page}
                  className="grid size-8 place-items-center text-xs text-plp-secondary"
                >
                  ...
                </span>
              );
            }

            const className = `grid size-8 place-items-center rounded-lg text-xs transition-colors ${
              page === currentPage
                ? 'bg-plp-accent font-bold text-white'
                : 'bg-transparent font-medium text-plp-secondary hover:text-plp-primary'
            }`;

            if (page === currentPage) {
              return (
                <button
                  key={page}
                  type="button"
                  aria-current="page"
                  disabled
                  className={className}
                >
                  {new Intl.NumberFormat('fa-IR').format(page)}
                </button>
              );
            }

            return (
              <a
                key={page}
                href={getPageHref(page)}
                onClick={(event) => {
                  event.preventDefault();
                  if (isNavigating) return;
                  setIsPaginationMode(true);
                  navigateToPage(page, 'replace');
                }}
                className={className}
              >
                {new Intl.NumberFormat('fa-IR').format(page)}
              </a>
            );
          })}

          <button
            type="button"
            aria-label="صفحه بعدی"
            disabled={currentPage === lastPage || isNavigating}
            onClick={() => {
              setIsPaginationMode(true);
              navigateToPage(Math.min(lastPage, currentPage + 1), 'replace');
            }}
            className="grid size-8 place-items-center rounded-full bg-white text-plp-muted disabled:pointer-events-none disabled:opacity-40"
          >
            <ArrowLeftSLineIcon className="size-4" />
          </button>
        </nav>
      )}
    </div>
  );
}
